import type { DeviceCapabilities } from "@paperclip-cloud/shared";
import { Cpu, HardDrive, Monitor, Tag } from "lucide-react";

export interface CapabilityBadgesProps {
  /** May be null/undefined for devices that paired but never reported caps. */
  caps: DeviceCapabilities | null | undefined;
  /** When true, show the full capabilities list with labels. */
  expanded?: boolean;
  max?: number;
}

const OS_LABEL: Record<string, string> = {
  win32: "Windows",
  darwin: "macOS",
  linux: "Linux",
};

export function CapabilityBadges({ caps, expanded = false, max = 4 }: CapabilityBadgesProps) {
  // Defensive: hub *should* always send a populated capabilities object now,
  // but older device rows or mid-reconnect races can still produce null.
  // Render a single "unknown" badge rather than crashing the whole tree.
  if (!caps) {
    return (
      <div className="flex flex-wrap items-center gap-[6px]">
        <span className="badge text-tertiary">unknown</span>
      </div>
    );
  }

  const items: Array<{ icon: React.ReactNode; label: string; key: string }> = [];

  items.push({
    key: "os",
    icon: <Monitor size={11} />,
    label: `${OS_LABEL[caps.os] ?? caps.os} · ${caps.arch}`,
  });
  items.push({
    key: "cpu",
    icon: <Cpu size={11} />,
    label: expanded ? `${caps.cpu.cores}c · ${caps.cpu.model}` : `${caps.cpu.cores}c`,
  });
  items.push({
    key: "ram",
    icon: <HardDrive size={11} />,
    label: `${caps.memory_gb}GB`,
  });
  if (caps.gpu.length > 0) {
    const g = caps.gpu[0];
    items.push({
      key: "gpu",
      icon: <span aria-hidden="true">▲</span>,
      label: expanded ? `${g.model} · ${g.vram_gb}GB` : `${g.vram_gb}G GPU`,
    });
  }
  for (const tag of caps.tags) {
    items.push({ key: `tag-${tag}`, icon: <Tag size={11} />, label: tag });
  }

  const shown = expanded ? items : items.slice(0, max);
  const hidden = items.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {shown.map((it) => (
        <span key={it.key} className="badge">
          <span className="text-tertiary inline-flex" aria-hidden="true">
            {it.icon}
          </span>
          <span>{it.label}</span>
        </span>
      ))}
      {hidden > 0 && (
        <span className="badge" title={items.slice(max).map((i) => i.label).join(", ")}>
          +{hidden}
        </span>
      )}
    </div>
  );
}
